<template>
  <b-modal ref="modal" :id="modalId" centered hide-footer title="Training Detail" size="xl">
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-danger" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading training detail...</p>
    </div>

    <div v-else-if="data">
      <!-- Course Content -->
      <div class="row g-0">
        <!-- Full Width - Course Info -->
        <div class="col-md-12">
          <!-- Header Section -->
          <div class="row mb-4">
            <div class="col-md-7 d-flex flex-column justify-content-between">
              <div class="d-flex flex-column">
                <!-- Status Badge -->
                <div class="mb-3">
                  <span :class="getTrainingStatusClass(data.status)">
                    {{ snakeToTitle(data.status) }}
                  </span>
                </div>
                <ApprovalStepper :item="data" />

                <!-- Course Title -->
                <h3 class="mb-3 font-weight-bold" style="color: #1a202c">
                  {{ data.training?.title || "-" }}
                </h3>

                <!-- Category Badges -->
                <div class="mb-4 d-flex gap-2">
                  <span class="badge-icon bg-danger-soft text-danger">
                    <i :class="getTrainingTypeIcon(data.training?.type?.name)"></i>
                  </span>
                  <span class="badge-custom bg-warning-soft text-warning">
                    {{ (data.competency && data.competency.name) || "-" }}
                  </span>
                  <!-- <span class="badge-custom bg-info-soft text-info">
                    {{ (data.competency && data.competency[0]?.type) || "-" }}
                  </span> -->
                </div>
              </div>

              <!-- Stats -->
              <div class="row p-3 rounded" style="background-color: #fafafa">
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted gap-1">
                    <i class="fe fe-clock me-2" style="font-size: 1.1rem"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem">Learning Hours</small>
                      <strong class="text-dark">{{ data.training.hour ? Number(data.training.hour) : "-" }} hrs</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted gap-1">
                    <i class="fe fe-tag me-2" style="font-size: 1.1rem"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem">Price</small>
                      <strong class="text-dark">{{ formatCurrency(data.training.currency, data.training.price) }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted gap-1">
                    <i class="fe fe-users me-2" style="font-size: 1.1rem"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem">Participants</small>
                      <strong class="text-dark">{{ data.training.total_participants }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side - Course Image -->
            <div class="col-md-5">
              <img
                :src="data.training.cover_image_path || `https://dummyimage.com/300x200/FAFAFA/000000&text=${data.training.title || 'Course'}`"
                alt="Course Thumbnail"
                class="w-100 rounded"
                style="height: 280px; object-fit: cover"
              />
            </div>
          </div>

          <!-- Tabs -->
          <ul class="nav nav-tabs mb-3" role="tablist">
            <li class="nav-item">
              <a
                :class="data.training.platform === 'My Learning' ? 'nav-link active' : 'nav-link'"
                data-bs-toggle="tab"
                href="#description"
              >
                Description
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#attributes"> Attributes </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#platform"> Platform </a>
            </li>
            <li v-if="data.training.platform !== 'My Learning'" class="nav-item">
              <a
                :class="data.training.platform !== 'My Learning' ? 'nav-link active' : 'nav-link'"
                data-bs-toggle="tab"
                href="#schedule"
              >
                Schedule
              </a>
            </li>
          </ul>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- Description Tab -->
            <div
              :class="data.training.platform !== 'Other' ? 'tab-pane fade show active' : 'tab-pane fade'"
              id="description"
            >
              <p class="mb-3" style="line-height: 1.8; font-size: 0.9rem">
                {{ data.training.description || "No description available." }}
              </p>

              <!-- Related Competencies -->
              <!-- <div class="mt-4" v-if="data.training?.competency_relations?.length > 0">
                <h6 class="font-weight-bold mb-3">Related Competencies</h6>
                <div class="d-flex gap-2 flex-wrap">
                  <span
                    v-for="(comp, index) in data.training?.competency_relations"
                    :key="index"
                    class="badge bg-white text-dark border px-3 py-2"
                    style="font-weight: 500"
                  >
                    {{ comp.competency ? comp.competency.name : "-" }} <span class="text-muted">|</span>
                    <i class="fe fe-star text-warning"></i>
                    {{ comp.proficiency ? comp.proficiency : "-" }}
                  </span>
                </div>
                <p class="text-muted mt-3 mb-0" style="font-size: 0.75rem">
                  **Notes: <i class="fe fe-star text-warning"></i> Levels of proficiency 1-5
                </p>
              </div> -->
            </div>

            <!-- TabAttributes -->
            <div class="tab-pane fade" id="attributes">
              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Course Method -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course Method</label>
                    <p class="text-dark font-weight-bold mb-0">
                      {{ data.training?.method?.name || data.training?.events[0]?.method?.name || "-" }}
                    </p>
                  </div>

                  <!-- Course Category -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course Category</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.category?.title || "-" }}</p>
                  </div>

                  <!-- Course URL -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course URL</label>
                    <div class="d-flex align-items-center gap-2">
                      <a
                        v-if="data.training.website"
                        :href="data.training.website"
                        target="_blank"
                        class="text-primary text-truncate"
                        style="max-width: 80%; text-decoration: none"
                      >
                        {{ data.training.website }}
                      </a>
                      <span v-else class="text-muted">-</span>
                      <a
                        v-if="data.training.website"
                        :href="data.training.website"
                        target="_blank"
                        class="btn btn-sm btn-danger d-flex align-items-center justify-content-center"
                        style="width: 32px; height: 32px; padding: 0; border-radius: 4px"
                      >
                        <i class="fe fe-send" style="font-size: 14px"></i>
                      </a>
                    </div>
                  </div>

                  <!-- Department -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Department</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training.department || "-" }}</p>
                  </div>

                  <!-- Division -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Division</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training.division || "-" }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Course Type -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course Type</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.type?.name || "-" }}</p>
                  </div>

                  <!-- Certificate Validity Period -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Certificate Validity Period (Months)</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.certificate_validity_period || "-" }}</p>
                  </div>

                  <!-- Course Scope -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Course Scope</label>
                    <div>
                      <span
                        v-for="scope in data.training?.training_scope"
                        :key="scope"
                        class="badge badge-dark font-weight-bold mr-1"
                      >
                        {{ scope }}
                      </span>
                    </div>
                  </div>

                  <!-- STEM Category -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">STEM Category</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training.stem_category || "-" }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Platform Tab -->
            <div class="tab-pane fade" id="platform">
              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Platform Name -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Platform Name</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.platform || "-" }}</p>
                  </div>

                  <!-- Training Provider (LDP) -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Training Provider</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.name || "-" }}</p>
                  </div>

                  <!-- Provider Website -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Provider Website</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.website || "-" }}</p>
                  </div>

                  <!-- Email -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Email</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.email || "-" }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Provider Name (LSP) -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Provider Name (LSP)</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.lsp?.name || "-" }}</p>
                  </div>

                  <!-- Address -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Address</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.address || "-" }}</p>
                  </div>

                  <!-- Contact Person -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Contact Person</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.contact_person || "-" }}</p>
                  </div>

                  <!-- Phone -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa">
                    <label class="text-muted small mb-2">Phone</label>
                    <p class="text-dark font-weight-bold mb-0">{{ data.training?.provider?.phone || "-" }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Schedule Tab -->
            <div
              v-if="data.training.platform !== 'My Learning'"
              :class="data.training.platform !== 'My Learning' ? 'tab-pane fade show active' : 'tab-pane fade'"
              id="schedule"
            >
              <!-- Schedule Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="rounded" style="background-color: #f8f9fa">
                    <tr>
                      <th class="text-muted small font-weight-normal">BATCH</th>
                      <th class="text-muted small font-weight-normal">PENYELENGGARA</th>
                      <th class="text-muted small font-weight-normal">QUOTA</th>
                      <th class="text-muted small font-weight-normal">USED QUOTA</th>
                      <th class="text-muted small font-weight-normal">REMAINING QUOTA</th>
                      <th class="text-muted small font-weight-normal">SCHEDULE</th>
                      <th class="text-muted small font-weight-normal">STATUS</th>
                      <th class="text-muted small font-weight-normal">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Empty State -->
                    <tr v-if="data?.training?.events?.length === 0">
                      <td colspan="8" class="text-center py-5">
                        <i class="fe fe-inbox text-muted" style="font-size: 3rem"></i>
                        <p class="text-muted mt-2">No batches available</p>
                      </td>
                    </tr>

                    <!-- Batch Rows -->
                    <tr v-for="(batch, index) in data?.training?.events" :key="batch.id || index">
                      <td class="align-middle">{{ formatBatchLabel(batch) }}</td>
                      <td class="align-middle">{{ formatPenyelenggara(batch.penyelenggara) }}</td>
                      <td class="align-middle">{{ batch.quota || "-" }}</td>
                      <td class="align-middle">{{ batch?.participant_count }}</td>
                      <td class="align-middle">{{ batch?.remaining_quota || "-" }}</td>
                      <td class="align-middle">
                        <div v-if="batch.schedules && batch.schedules.length > 0">
                          <div v-for="(schedule, idx) in batch.schedules" :key="idx" :class="{ 'mt-2': idx > 0 }">
                            <strong class="d-block">{{ formatLongDate(schedule.date) || "-" }}</strong>
                            <small class="text-muted"
                              >{{ formatTime(schedule.start_time) || "-" }} -
                              {{ formatTime(schedule.end_time) || "-" }}</small
                            >
                          </div>
                        </div>
                        <span v-else class="text-muted">No schedule</span>
                      </td>
                      <td class="align-middle">
                        <div v-if="batch.schedules && batch.schedules.length > 0">
                          <div v-for="(schedule, idx) in batch.schedules" :key="idx" :class="{ 'mt-4': idx > 0 }">
                            <strong class="d-block">{{ snakeToTitle(schedule.status) || "-" }}</strong>
                          </div>
                        </div>
                        <span v-else class="text-muted">{{ batch.status }}</span>
                      </td>
                      <td class="align-middle">
                        <div class="d-flex gap-2">
                          <span :class="getTrainingStatusClass(batch?.status)">
                            {{ modifyStatus(batch?.status) || "" }}
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  formatCurrency,
  formatLongDate,
  formatTime,
  modifyStatus,
  snakeToTitle,
  getTrainingStatusClass,
} from "../utils/utils";
import { formatBatchLabel, formatPenyelenggara } from "@/utils/batchIdentity";
import ApprovalStepper from "@/components/ApprovalStepper.vue";

export default {
  name: "StructuredLearningModal",

  components: { ApprovalStepper },

  props: {
    modalId: { type: String, default: "structured-learning-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {};
  },

  computed: {},

  methods: {
    formatCurrency,
    snakeToTitle,
    getTrainingStatusClass,
    formatLongDate,
    formatTime,
    modifyStatus,
    formatBatchLabel,
    formatPenyelenggara,

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || "";

      if (type.includes("certification") && !type.includes("semi")) {
        return "fe fe-file-text";
      } else if (type.includes("semi")) {
        return "fe fe-shield";
      } else if (type.includes("training")) {
        return "fe fe-user";
      }

      return "fe fe-award";
    },
  },
};
</script>

<style scoped>
.w-50 {
  width: 50%;
}
</style>
